import type { HTMLAttributes, ReactNode, TdHTMLAttributes } from 'react';
import { cn } from '../../lib/cn';
import { useEdgeBleed } from './useEdgeBleed';

/**
 * A table that scrolls sideways inside its own box. It is drawn as the whole content of the card
 * that frames it (`<Card><Table>…</Table></Card>`), so on a desktop screen too narrow for it, it is
 * that card - its parent - that runs off the page's right edge (useEdgeBleed): the frame stays whole
 * and the cut at the edge says the table scrolls.
 */
export function Table({ children }: { children: ReactNode }) {
  const bleedRef = useEdgeBleed<HTMLDivElement>('parent');
  return (
    <div ref={bleedRef} className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">{children}</table>
    </div>
  );
}

export function THead({ children }: { children: ReactNode }) {
  return (
    <thead className="border-b border-line text-left text-xs font-semibold uppercase tracking-wide text-ink-muted">
      {children}
    </thead>
  );
}

export function TH({ className, children }: { className?: string; children?: ReactNode }) {
  return <th className={cn('px-4 py-2.5 font-semibold', className)}>{children}</th>;
}

export function TBody({ children }: { children: ReactNode }) {
  return <tbody className="divide-y divide-line-soft">{children}</tbody>;
}

export function TR({ className, ...rest }: HTMLAttributes<HTMLTableRowElement>) {
  return <tr className={cn('hover:bg-hover/60', className)} {...rest} />;
}

export function TD({ className, ...rest }: TdHTMLAttributes<HTMLTableCellElement>) {
  return <td className={cn('px-4 py-3 align-middle text-ink-strong', className)} {...rest} />;
}
